import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService, CoreServicesService, GraphqlApiService } from 'ngx-core-services';
import { ModbusServicesService } from 'ngx-modbus';
import { BlockTranslationsService, SystemObserverService } from 'ngx-zcs-blocks';
import { ErrorSinkService, ToastFacade } from 'ngx-zcs-components';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { User } from 'firebase/auth';

import { environment } from '../environments/environment';
import { DevEnvPreferenceService } from './shared/services';

const setUser = (user?: User | null) => {
  if (!user) {
    return;
  }

  Sentry.setUser({ id: user.uid, email: user?.email ?? 'unknown' });
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private subscription?: Subscription;

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly coreServicesService: CoreServicesService,
    private readonly blockTranslationsService: BlockTranslationsService,
    private readonly devEnvPreferenceService: DevEnvPreferenceService,
    private readonly errorSink: ErrorSinkService,
    private readonly graphqlApiService: GraphqlApiService,
    private readonly modbusServicesService: ModbusServicesService,
    private readonly platform: Platform,
    private readonly ref: ChangeDetectorRef,
    private readonly systemObserverService: SystemObserverService,
    private readonly toastFacade: ToastFacade,
    private readonly translateService: TranslateService,
  ) {
    this.initializeApp();

    // ErrorSink → ToastFacade: single point of toast display for modbus errors
    this.errorSink.toasts$.subscribe((error) => {
      this.toastFacade.present(error.message, error.title, { severity: 'error' });
    });
  }

  ngOnInit(): void {
    this.subscription = this.systemObserverService.getSystemsWithMultipleObservers().subscribe((_) => {
      setTimeout(() => this.ref.detectChanges());
    });

    this.authenticationService.user$.subscribe(setUser);
    this.applySavedAppSyncEnv();
  }

  private applySavedAppSyncEnv(): void {
    this.authenticationService.user$.pipe(
      filter((user): user is User => !!user && user.emailVerified),
      take(1),
    ).subscribe(async (user) => {
      try {
        const saved = await this.devEnvPreferenceService.read(user.uid);
        if (!saved) {
          return;
        }
        const isKnown = environment.aws.appsync.list.some((e) => e.url === saved.url);
        if (!isKnown) {
          return;
        }
        await this.graphqlApiService.build(saved.region, saved.url, saved.desc);
      } catch {
        // ignore — keep default environment on any read/build failure
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  async initializeApp() {
    await this.platform.ready();

    this.toastFacade.setToastOptions({ type: 'browser' });
    this.translateService.onDefaultLangChange.subscribe(() => {
      this.updateCoreService();
      this.updateModbusService();
      this.updateBlockTranslateService();
    });

    this.translateService.onLangChange.subscribe(() => {
      this.updateCoreService();
      this.updateModbusService();
      this.updateBlockTranslateService();
    });

    const preferenceLanguage = localStorage.getItem('language');
    if (preferenceLanguage) {
      await this.translateService.use(preferenceLanguage).toPromise();
    }
  }

  updateModbusService() {
    this.modbusServicesService.updateLabels({
      'AutoTestPage.Forbidden': this.translateService.instant('i18n.AutoTestPage.Forbidden'),
      'AutoTestPage.Incorrect': this.translateService.instant('i18n.AutoTestPage.Incorrect'),
      'AutoTestPage.NoResponse': this.translateService.instant('i18n.AutoTestPage.NoResponse'),
      'AutoTestPage.QuickRunning': this.translateService.instant('i18n.AutoTestPage.QuickRunning'),
      'AutoTestPage.Refused': this.translateService.instant('i18n.AutoTestPage.Refused'),
      'AutoTestPage.resolveSuccess': this.translateService.instant('i18n.AutoTestPage.resolveSuccess'),
      'AutoTestPage.StandardRunning': this.translateService.instant('i18n.AutoTestPage.StandardRunning'),
      'AutoTestPage.StorageFailed': this.translateService.instant('i18n.AutoTestPage.StorageFailed'),
      'Bluetooth.enableBluetoothIsRequired': this.translateService.instant('i18n.Bluetooth.enableBluetoothIsRequired'),
      'Bluetooth.retryAfterEnableBluetooth': this.translateService.instant('i18n.Bluetooth.retryAfterEnableBluetooth'),
      'BTS5k.bduId': this.translateService.instant('i18n.BTS5k.bduId'),
      'BTS5k.bduSn': this.translateService.instant('i18n.BTS5k.bduSn'),
      'BTS5k.bmsSysTime': this.translateService.instant('i18n.BTS5k.bmsSysTime'),
      'BTS5k.bmsVersion': this.translateService.instant('i18n.BTS5k.bmsVersion'),
      'BTS5k.fwVersion': this.translateService.instant('i18n.BTS5k.fwVersion'),
      'BTS5k.packRtCellMaxVoltage': this.translateService.instant('i18n.BTS5k.packRtCellMaxVoltage'),
      'BTS5k.packRtCellMinVoltage': this.translateService.instant('i18n.BTS5k.packRtCellMinVoltage'),
      'BTS5k.packRtCurrent': this.translateService.instant('i18n.BTS5k.packRtCurrent'),
      'BTS5k.packRtCycles': this.translateService.instant('i18n.BTS5k.packRtCycles'),
      'BTS5k.packRtSn': this.translateService.instant('i18n.BTS5k.packRtSn'),
      'BTS5k.packRtSoc': this.translateService.instant('i18n.BTS5k.packRtSoc'),
      'BTS5k.packRtTemperature1Pack': this.translateService.instant('i18n.BTS5k.packRtTemperature1Pack'),
      'BTS5k.packRtTemperature2Pack': this.translateService.instant('i18n.BTS5k.packRtTemperature2Pack'),
      'BTS5k.packRtTemperature3Pack': this.translateService.instant('i18n.BTS5k.packRtTemperature3Pack'),
      'BTS5k.packRtTemperature4Pack': this.translateService.instant('i18n.BTS5k.packRtTemperature4Pack'),
      'BTS5k.packRtTotalVoltage': this.translateService.instant('i18n.BTS5k.packRtTotalVoltage'),
      'BTS5k.pcuDcHighVoltage': this.translateService.instant('i18n.BTS5k.pcuDcHighVoltage'),
      'BTS5k.pcuDcLowVoltage': this.translateService.instant('i18n.BTS5k.pcuDcLowVoltage'),
      'BTS5k.pcuInternalTemperature': this.translateService.instant('i18n.BTS5k.pcuInternalTemperature'),
      'BTS5k.pcuLowCurrent': this.translateService.instant('i18n.BTS5k.pcuLowCurrent'),
      'BTS5k.pcuLowPower': this.translateService.instant('i18n.BTS5k.pcuLowPower'),
      'BTS5k.pcuRadiatorTemperature1': this.translateService.instant('i18n.BTS5k.pcuRadiatorTemperature1'),
      'BTS5k.pcuRadiatorTemperature2': this.translateService.instant('i18n.BTS5k.pcuRadiatorTemperature2'),
      'BTS5k.pcuSn': this.translateService.instant('i18n.BTS5k.pcuSn'),
      'BTS5k.pcuSoftwareVersion': this.translateService.instant('i18n.BTS5k.pcuSoftwareVersion'),
      'BTS5k.pcuWorkState': this.translateService.instant('i18n.BTS5k.pcuWorkState'),
      'BTS5k.soh': this.translateService.instant('i18n.BTS5k.soh'),
      'Common.disabled': this.translateService.instant('i18n.Common.disabled'),
      'Common.enabled': this.translateService.instant('i18n.Common.enabled'),
      'Common.friday': this.translateService.instant('i18n.Common.friday'),
      'Common.monday': this.translateService.instant('i18n.Common.monday'),
      'Common.notUse': this.translateService.instant('i18n.Common.notUse'),
      'Common.notUsed': this.translateService.instant('i18n.Common.notUsed'),
      'Common.saturday': this.translateService.instant('i18n.Common.saturday'),
      'Common.sunday': this.translateService.instant('i18n.Common.sunday'),
      'Common.thursday': this.translateService.instant('i18n.Common.thursday'),
      'Common.tuesday': this.translateService.instant('i18n.Common.tuesday'),
      'Common.unableToOpenSocket': this.translateService.instant('i18n.Common.unableToOpenSocket'),
      'Common.unsupportedStickLogger': this.translateService.instant('i18n.Common.unsupportedStickLogger'),
      'Common.wednesday': this.translateService.instant('i18n.Common.wednesday'),
      'DeviceInfoPage.activePowerDerate': this.translateService.instant('i18n.DeviceInfoPage.activePowerDerate'),
      'DeviceInfoPage.antiBackflowOverloadControl': this.translateService.instant('i18n.DeviceInfoPage.antiBackflowOverloadControl'),
      'DeviceInfoPage.battery': this.translateService.instant('i18n.DeviceInfoPage.battery'),
      'DeviceInfoPage.batteryChargingPowerDerateByLowVoltage': this.translateService.instant(
        'i18n.DeviceInfoPage.batteryChargingPowerDerateByLowVoltage',
      ),
      'DeviceInfoPage.batteryChargingPowerDerateLeadingHighVoltage': this.translateService.instant(
        'i18n.DeviceInfoPage.batteryChargingPowerDerateLeadingHighVoltage',
      ),
      'DeviceInfoPage.chargeEnable': this.translateService.instant('i18n.DeviceInfoPage.chargeEnable'),
      'DeviceInfoPage.disableAntiReflux': this.translateService.instant('i18n.DeviceInfoPage.disableAntiReflux'),
      'DeviceInfoPage.disableBatteryActive': this.translateService.instant('i18n.DeviceInfoPage.disableBatteryActive'),
      'DeviceInfoPage.disableBatteryActiveOneShot': this.translateService.instant('i18n.DeviceInfoPage.disableBatteryActiveOneShot'),
      'DeviceInfoPage.disabled': this.translateService.instant('i18n.DeviceInfoPage.disabled'),
      'DeviceInfoPage.disableEmergencyPowerSupply': this.translateService.instant('i18n.DeviceInfoPage.disableEmergencyPowerSupply'),
      'DeviceInfoPage.disableScanCurve': this.translateService.instant('i18n.DeviceInfoPage.disableScanCurve'),
      'DeviceInfoPage.disableUnbalancedSupport': this.translateService.instant('i18n.DeviceInfoPage.disableUnbalancedSupport'),
      'DeviceInfoPage.dischargeEnable': this.translateService.instant('i18n.DeviceInfoPage.dischargeEnable'),
      'DeviceInfoPage.enableAntiRefluxMeanValue': this.translateService.instant('i18n.DeviceInfoPage.enableAntiRefluxMeanValue'),
      'DeviceInfoPage.enableAntiRefluxMinPower': this.translateService.instant('i18n.DeviceInfoPage.enableAntiRefluxMinPower'),
      'DeviceInfoPage.enableBatteryActive': this.translateService.instant('i18n.DeviceInfoPage.enableBatteryActive'),
      'DeviceInfoPage.enableBatteryActiveOneShot': this.translateService.instant('i18n.DeviceInfoPage.enableBatteryActiveOneShot'),
      'DeviceInfoPage.enableEmergencyPowerSupplyDisableColdStart': this.translateService.instant(
        'i18n.DeviceInfoPage.enableEmergencyPowerSupplyDisableColdStart',
      ),
      'DeviceInfoPage.enableEmergencyPowerSupplyEnableColdStart': this.translateService.instant(
        'i18n.DeviceInfoPage.enableEmergencyPowerSupplyEnableColdStart',
      ),
      'DeviceInfoPage.enableScanCurve': this.translateService.instant('i18n.DeviceInfoPage.enableScanCurve'),
      'DeviceInfoPage.enableUnbalancedSupport': this.translateService.instant('i18n.DeviceInfoPage.enableUnbalancedSupport'),
      'DeviceInfoPage.groundTestingEnableBit': this.translateService.instant('i18n.DeviceInfoPage.groundTestingEnableBit'),
      'DeviceInfoPage.independentMode': this.translateService.instant('i18n.DeviceInfoPage.independentMode'),
      'DeviceInfoPage.insulationResistanceTestingEnableBit': this.translateService.instant(
        'i18n.DeviceInfoPage.insulationResistanceTestingEnableBit',
      ),
      'DeviceInfoPage.logicInterfaceDrm1-8': this.translateService.instant('i18n.DeviceInfoPage.logicInterfaceDrm1-8'),
      'DeviceInfoPage.master': this.translateService.instant('i18n.DeviceInfoPage.master'),
      'DeviceInfoPage.minutes': this.translateService.instant('i18n.DeviceInfoPage.minutes'),
      'DeviceInfoPage.notAvailable': this.translateService.instant('i18n.DeviceInfoPage.notAvailable'),
      'DeviceInfoPage.parallelMode': this.translateService.instant('i18n.DeviceInfoPage.parallelMode'),
      'DeviceInfoPage.powerDerateLeadingByHighACVoltage': this.translateService.instant(
        'i18n.DeviceInfoPage.powerDerateLeadingByHighACVoltage',
      ),
      'DeviceInfoPage.pv': this.translateService.instant('i18n.DeviceInfoPage.pv'),
      'DeviceInfoPage.pvAndBattery': this.translateService.instant('i18n.DeviceInfoPage.pvAndBattery'),
      'DeviceInfoPage.remotelyOnOffControl': this.translateService.instant('i18n.DeviceInfoPage.remotelyOnOffControl'),
      'DeviceInfoPage.seconds': this.translateService.instant('i18n.DeviceInfoPage.seconds'),
      'DeviceInfoPage.slave': this.translateService.instant('i18n.DeviceInfoPage.slave'),
      'DeviceInfoPage.voltageDropoutPTCurveControl': this.translateService.instant('i18n.DeviceInfoPage.voltageDropoutPTCurveControl'),
      'report.ActiveOutputDownSpeed': this.translateService.instant('i18n.report.ActiveOutputDownSpeed'),
      'report.ActiveOutputLimit': this.translateService.instant('i18n.report.ActiveOutputLimit'),
      'report.AntiBackflowOverloadEnable': this.translateService.instant('i18n.report.AntiBackflowOverloadEnable'),
      'report.BatteryChargingPowerDerateByLowVoltage': this.translateService.instant('i18n.report.BatteryChargingPowerDerateByLowVoltage'),
      'report.BatteryChargingPowerDerateLeadingHighVoltage': this.translateService.instant(
        'i18n.report.BatteryChargingPowerDerateLeadingHighVoltage',
      ),
      'report.ChargeReactiveEnable': this.translateService.instant('i18n.report.ChargeReactiveEnable'),
      'report.ChgDerateMinPower': this.translateService.instant('i18n.report.ChgDerateMinPower'),
      'report.ChgDerateVoltEnd': this.translateService.instant('i18n.report.ChgDerateVoltEnd'),
      'report.ChgDerateVoltStart': this.translateService.instant('i18n.report.ChgDerateVoltStart'),
      'report.ConnectWaitTime': this.translateService.instant('i18n.report.ConnectWaitTime'),
      'report.DCIConfig': this.translateService.instant('i18n.report.DCIConfig'),
      'report.DCIFirstProtectionProportion': this.translateService.instant('i18n.report.DCIFirstProtectionProportion'),
      'report.DCIFirstProtectionTime': this.translateService.instant('i18n.report.DCIFirstProtectionTime'),
      'report.DCIFirstProtectionValue': this.translateService.instant('i18n.report.DCIFirstProtectionValue'),
      'report.DCIPhaseRTestingValue': this.translateService.instant('i18n.report.DCIPhaseRTestingValue'),
      'report.DCIPhaseSTestingValue': this.translateService.instant('i18n.report.DCIPhaseSTestingValue'),
      'report.DCIPhaseTTestingValue': this.translateService.instant('i18n.report.DCIPhaseTTestingValue'),
      'report.DCISecondProtectionProportion': this.translateService.instant('i18n.report.DCISecondProtectionProportion'),
      'report.DCISecondProtectionTime': this.translateService.instant('i18n.report.DCISecondProtectionTime'),
      'report.DCISecondProtectionValue': this.translateService.instant('i18n.report.DCISecondProtectionValue'),
      'report.DCITestingEnable': this.translateService.instant('i18n.report.DCITestingEnable'),
      'report.DCIThirdProtectionProportion': this.translateService.instant('i18n.report.DCIThirdProtectionProportion'),
      'report.DCIThirdProtectionTime': this.translateService.instant('i18n.report.DCIThirdProtectionTime'),
      'report.DCIThirdProtectionValue': this.translateService.instant('i18n.report.DCIThirdProtectionValue'),
      'report.DeratingMode': this.translateService.instant('i18n.report.DeratingMode'),
      'report.DRM0': this.translateService.instant('i18n.report.DRM0'),
      'report.EnableDerateStartFromRatedPower': this.translateService.instant('i18n.report.EnableDerateStartFromRatedPower'),
      'report.EnableDeratingSlopeCalculateByCurrentPower': this.translateService.instant(
        'i18n.report.EnableDeratingSlopeCalculateByCurrentPower',
      ),
      'report.EnableHigherFrequencyPowerDerate': this.translateService.instant('i18n.report.EnableHigherFrequencyPowerDerate'),
      'report.EnableHigherFrequencyPowerReload': this.translateService.instant('i18n.report.EnableHigherFrequencyPowerReload'),
      'report.EnableLowerFrequencyPowerDerate': this.translateService.instant('i18n.report.EnableLowerFrequencyPowerDerate'),
      'report.EnableLowerFrequencyPowerReload': this.translateService.instant('i18n.report.EnableLowerFrequencyPowerReload'),
      'report.EnableReactiveMode3': this.translateService.instant('i18n.report.EnableReactiveMode3'),
      'report.EnableReactivePower': this.translateService.instant('i18n.report.EnableReactivePower'),
      'report.EnergyStorageSegmentationCurveEnable': this.translateService.instant('i18n.report.EnergyStorageSegmentationCurveEnable'),
      'report.ESSFrequencyRecoverMax': this.translateService.instant('i18n.report.ESSFrequencyRecoverMax'),
      'report.ESSFrequencyRecoverMin': this.translateService.instant('i18n.report.ESSFrequencyRecoverMin'),
      'report.ESSOverfrequencyEnd': this.translateService.instant('i18n.report.ESSOverfrequencyEnd'),
      'report.ESSOverFrequencyReloadDelay': this.translateService.instant('i18n.report.ESSOverFrequencyReloadDelay'),
      'report.ESSOverFrequencyReloadSpeed': this.translateService.instant('i18n.report.ESSOverFrequencyReloadSpeed'),
      'report.ESSOverfrequencyReloadStart': this.translateService.instant('i18n.report.ESSOverfrequencyReloadStart'),
      'report.ESSOverFrequencyResponseDelay': this.translateService.instant('i18n.report.ESSOverFrequencyResponseDelay'),
      'report.ESSOverFrequencySlope': this.translateService.instant('i18n.report.ESSOverFrequencySlope'),
      'report.ESSOverFrequencyStart': this.translateService.instant('i18n.report.ESSOverFrequencyStart'),
      'report.ESSOverFrequencyTransition': this.translateService.instant('i18n.report.ESSOverFrequencyTransition'),
      'report.ESSUnderfrequencyEnd': this.translateService.instant('i18n.report.ESSUnderfrequencyEnd'),
      'report.ESSUnderFrequencyReloadDelay': this.translateService.instant('i18n.report.ESSUnderFrequencyReloadDelay'),
      'report.ESSUnderFrequencyReloadSpeed': this.translateService.instant('i18n.report.ESSUnderFrequencyReloadSpeed'),
      'report.ESSUnderfrequencyReloadStart': this.translateService.instant('i18n.report.ESSUnderfrequencyReloadStart'),
      'report.ESSUnderFrequencyResponseDelay': this.translateService.instant('i18n.report.ESSUnderFrequencyResponseDelay'),
      'report.ESSUnderFrequencySlope': this.translateService.instant('i18n.report.ESSUnderFrequencySlope'),
      'report.ESSUnderFrequencyStart': this.translateService.instant('i18n.report.ESSUnderFrequencyStart'),
      'report.ESSUnderFrequencyTransition': this.translateService.instant('i18n.report.ESSUnderFrequencyTransition'),
      'report.FirstOverfrequencyProtectionTime': this.translateService.instant('i18n.report.FirstOverfrequencyProtectionTime'),
      'report.FirstOverfrequencyProtectionValue': this.translateService.instant('i18n.report.FirstOverfrequencyProtectionValue'),
      'report.FirstOvervoltageProtectionTime': this.translateService.instant('i18n.report.FirstOvervoltageProtectionTime'),
      'report.FirstOvervoltageProtectionValue': this.translateService.instant('i18n.report.FirstOvervoltageProtectionValue'),
      'report.FirstUnderfrequencyProtectionTime': this.translateService.instant('i18n.report.FirstUnderfrequencyProtectionTime'),
      'report.FirstUnderfrequencyProtectionValue': this.translateService.instant('i18n.report.FirstUnderfrequencyProtectionValue'),
      'report.FirstUndervoltageProtectionTime': this.translateService.instant('i18n.report.FirstUndervoltageProtectionTime'),
      'report.FirstUndervoltageProtectionValue': this.translateService.instant('i18n.report.FirstUndervoltageProtectionValue'),
      'report.FixedReactivePercentage': this.translateService.instant('i18n.report.FixedReactivePercentage'),
      'report.FreqHighLimit': this.translateService.instant('i18n.report.FreqHighLimit'),
      'report.FreqLowLimit': this.translateService.instant('i18n.report.FreqLowLimit'),
      'report.FrequencyConfig': this.translateService.instant('i18n.report.FrequencyConfig'),
      'report.FrequencyDerateConfig': this.translateService.instant('i18n.report.FrequencyDerateConfig'),
      'report.FrequencyMode': this.translateService.instant('i18n.report.FrequencyMode'),
      'report.FrequencyRecoverMax': this.translateService.instant('i18n.report.FrequencyRecoverMax'),
      'report.FrequencyRecoverMin': this.translateService.instant('i18n.report.FrequencyRecoverMin'),
      'report.GridVoltageDropMinPower': this.translateService.instant('i18n.report.GridVoltageDropMinPower'),
      'report.GridVoltageDropStart': this.translateService.instant('i18n.report.GridVoltageDropStart'),
      'report.GridVoltageDropStop': this.translateService.instant('i18n.report.GridVoltageDropStop'),
      'report.HighVoltEndValue4': this.translateService.instant('i18n.report.HighVoltEndValue4'),
      'report.HighVoltEndValue5': this.translateService.instant('i18n.report.HighVoltEndValue5'),
      'report.HighVoltStartValue4': this.translateService.instant('i18n.report.HighVoltStartValue4'),
      'report.HighVoltStartValue5': this.translateService.instant('i18n.report.HighVoltStartValue5'),
      'report.LockinPower4': this.translateService.instant('i18n.report.LockinPower4'),
      'report.LockinPower5': this.translateService.instant('i18n.report.LockinPower5'),
      'report.LockinV': this.translateService.instant('i18n.report.LockinV'),
      'report.LockoutPower4': this.translateService.instant('i18n.report.LockoutPower4'),
      'report.LockoutPower5': this.translateService.instant('i18n.report.LockoutPower5'),
      'report.LockoutV': this.translateService.instant('i18n.report.LockoutV'),
      'report.LogicDerateSpeed': this.translateService.instant('i18n.report.LogicDerateSpeed'),
      'report.LogicInterface': this.translateService.instant('i18n.report.LogicInterface'),
      'report.LogicReloadSpeed': this.translateService.instant('i18n.report.LogicReloadSpeed'),
      'report.LowVoltEndValue4': this.translateService.instant('i18n.report.LowVoltEndValue4'),
      'report.LowVoltEndValue5': this.translateService.instant('i18n.report.LowVoltEndValue5'),
      'report.LowVoltStartValue4': this.translateService.instant('i18n.report.LowVoltStartValue4'),
      'report.LowVoltStartValue5': this.translateService.instant('i18n.report.LowVoltStartValue5'),
      'report.MaximumPowerCalculationEnable': this.translateService.instant('i18n.report.MaximumPowerCalculationEnable'),
      'report.MaxLaggingReactivePower4': this.translateService.instant('i18n.report.MaxLaggingReactivePower4'),
      'report.MaxLeadingReactivePower4': this.translateService.instant('i18n.report.MaxLeadingReactivePower4'),
      'report.MaxReactivePower5': this.translateService.instant('i18n.report.MaxReactivePower5'),
      'report.OverfrequencyEnd': this.translateService.instant('i18n.report.OverfrequencyEnd'),
      'report.OverfrequencyReloadDelay': this.translateService.instant('i18n.report.OverfrequencyReloadDelay'),
      'report.OverfrequencyReloadSpeed': this.translateService.instant('i18n.report.OverfrequencyReloadSpeed'),
      'report.OverfrequencyReloadStart': this.translateService.instant('i18n.report.OverfrequencyReloadStart'),
      'report.OverfrequencyResponseDelay': this.translateService.instant('i18n.report.OverfrequencyResponseDelay'),
      'report.OverfrequencySlope': this.translateService.instant('i18n.report.OverfrequencySlope'),
      'report.OverfrequencyStart': this.translateService.instant('i18n.report.OverfrequencyStart'),
      'report.OvervoltageDownSpeed': this.translateService.instant('i18n.report.OvervoltageDownSpeed'),
      'report.PhaseType': this.translateService.instant('i18n.report.PhaseType'),
      'report.PowerDerateLeadingByHighACVoltage': this.translateService.instant('i18n.report.PowerDerateLeadingByHighACVoltage'),
      'report.PowerFactor': this.translateService.instant('i18n.report.PowerFactor'),
      'report.PowerForLogic1': this.translateService.instant('i18n.report.PowerForLogic1'),
      'report.PowerForLogic2': this.translateService.instant('i18n.report.PowerForLogic2'),
      'report.PowerForLogic3': this.translateService.instant('i18n.report.PowerForLogic3'),
      'report.PowerForLogic4': this.translateService.instant('i18n.report.PowerForLogic4'),
      'report.PowerForLogic5': this.translateService.instant('i18n.report.PowerForLogic5'),
      'report.PowerForLogic6': this.translateService.instant('i18n.report.PowerForLogic6'),
      'report.PowerForLogic7': this.translateService.instant('i18n.report.PowerForLogic7'),
      'report.PowerForLogic8': this.translateService.instant('i18n.report.PowerForLogic8'),
      'report.PowerUpSpeed': this.translateService.instant('i18n.report.PowerUpSpeed'),
      'report.RatedFrequency': this.translateService.instant('i18n.report.RatedFrequency'),
      'report.RatedVoltage': this.translateService.instant('i18n.report.RatedVoltage'),
      'report.ReactiveConfig': this.translateService.instant('i18n.report.ReactiveConfig'),
      'report.ReactiveCos1': this.translateService.instant('i18n.report.ReactiveCos1'),
      'report.ReactiveCos2': this.translateService.instant('i18n.report.ReactiveCos2'),
      'report.ReactiveCos3': this.translateService.instant('i18n.report.ReactiveCos3'),
      'report.ReactiveCos4': this.translateService.instant('i18n.report.ReactiveCos4'),
      'report.ReactivedynamicValue1': this.translateService.instant('i18n.report.ReactivedynamicValue1'),
      'report.ReactivedynamicValue2': this.translateService.instant('i18n.report.ReactivedynamicValue2'),
      'report.ReactivedynamicValue3': this.translateService.instant('i18n.report.ReactivedynamicValue3'),
      'report.ReactivedynamicValue4': this.translateService.instant('i18n.report.ReactivedynamicValue4'),
      'report.ReactiveMode1': this.translateService.instant('i18n.report.ReactiveMode1'),
      'report.ReactiveMode2': this.translateService.instant('i18n.report.ReactiveMode2'),
      'report.ReactiveMode3': this.translateService.instant('i18n.report.ReactiveMode3'),
      'report.ReactiveMode4': this.translateService.instant('i18n.report.ReactiveMode4'),
      'report.ReactiveMode5': this.translateService.instant('i18n.report.ReactiveMode5'),
      'report.ReactiveMode6': this.translateService.instant('i18n.report.ReactiveMode6'),
      'report.ReactiveModeFourAustrianEnableBit': this.translateService.instant('i18n.report.ReactiveModeFourAustrianEnableBit'),
      'report.ReactivePowerMode': this.translateService.instant('i18n.report.ReactivePowerMode'),
      'report.ReactivePowerOffset4': this.translateService.instant('i18n.report.ReactivePowerOffset4'),
      'report.ReactivePowerStart4': this.translateService.instant('i18n.report.ReactivePowerStart4'),
      'report.ReactiveResponsePeriod': this.translateService.instant('i18n.report.ReactiveResponsePeriod'),
      'report.ReactiveResponseWaitTime4': this.translateService.instant('i18n.report.ReactiveResponseWaitTime4'),
      'report.ReactiveResponseWaitTime5': this.translateService.instant('i18n.report.ReactiveResponseWaitTime5'),
      'report.ReconnectFreqHighLimit': this.translateService.instant('i18n.report.ReconnectFreqHighLimit'),
      'report.ReconnectFreqLowLimit': this.translateService.instant('i18n.report.ReconnectFreqLowLimit'),
      'report.ReconnectPowerUpSpeed': this.translateService.instant('i18n.report.ReconnectPowerUpSpeed'),
      'report.ReconnectVoltHighLimit': this.translateService.instant('i18n.report.ReconnectVoltHighLimit'),
      'report.ReconnectVoltLowLimit': this.translateService.instant('i18n.report.ReconnectVoltLowLimit'),
      'report.ReconnectWaitTime': this.translateService.instant('i18n.report.ReconnectWaitTime'),
      'report.RefluxOVloadTime': this.translateService.instant('i18n.report.RefluxOVloadTime'),
      'report.RefluxPower': this.translateService.instant('i18n.report.RefluxPower'),
      'report.RemoteConfig': this.translateService.instant('i18n.report.RemoteConfig'),
      'report.RemoteOnOffControl': this.translateService.instant('i18n.report.RemoteOnOffControl'),
      'report.SecondOverfrequencyProtectionTime': this.translateService.instant('i18n.report.SecondOverfrequencyProtectionTime'),
      'report.SecondOverfrequencyProtectionValue': this.translateService.instant('i18n.report.SecondOverfrequencyProtectionValue'),
      'report.SecondOvervoltageProtectionTime': this.translateService.instant('i18n.report.SecondOvervoltageProtectionTime'),
      'report.SecondOvervoltageProtectionValue': this.translateService.instant('i18n.report.SecondOvervoltageProtectionValue'),
      'report.SecondUnderfrequencyProtectionTime': this.translateService.instant('i18n.report.SecondUnderfrequencyProtectionTime'),
      'report.SecondUnderfrequencyProtectionValue': this.translateService.instant('i18n.report.SecondUnderfrequencyProtectionValue'),
      'report.SecondUndervoltageProtectionTime': this.translateService.instant('i18n.report.SecondUndervoltageProtectionTime'),
      'report.SecondUndervoltageProtectionValue': this.translateService.instant('i18n.report.SecondUndervoltageProtectionValue'),
      'report.SlopeMode': this.translateService.instant('i18n.report.SlopeMode'),
      'report.TenMinOvervoltageProtectionValue': this.translateService.instant('i18n.report.TenMinOvervoltageProtectionValue'),
      'report.ThirdOverfrequencyProtectionTime': this.translateService.instant('i18n.report.ThirdOverfrequencyProtectionTime'),
      'report.ThirdOverfrequencyProtectionValue': this.translateService.instant('i18n.report.ThirdOverfrequencyProtectionValue'),
      'report.ThirdOvervoltageProtectionTime': this.translateService.instant('i18n.report.ThirdOvervoltageProtectionTime'),
      'report.ThirdOvervoltageProtectionValue': this.translateService.instant('i18n.report.ThirdOvervoltageProtectionValue'),
      'report.ThirdUnderfrequencyProtectionTime': this.translateService.instant('i18n.report.ThirdUnderfrequencyProtectionTime'),
      'report.ThirdUnderfrequencyProtectionValue': this.translateService.instant('i18n.report.ThirdUnderfrequencyProtectionValue'),
      'report.ThirdUndervoltageProtectionTime': this.translateService.instant('i18n.report.ThirdUndervoltageProtectionTime'),
      'report.ThirdUndervoltageProtectionValue': this.translateService.instant('i18n.report.ThirdUndervoltageProtectionValue'),
      'report.UnderfrequencyEnd': this.translateService.instant('i18n.report.UnderfrequencyEnd'),
      'report.UnderfrequencyReloadDelay': this.translateService.instant('i18n.report.UnderfrequencyReloadDelay'),
      'report.UnderfrequencyReloadSpeed': this.translateService.instant('i18n.report.UnderfrequencyReloadSpeed'),
      'report.UnderfrequencyReloadStart': this.translateService.instant('i18n.report.UnderfrequencyReloadStart'),
      'report.UnderfrequencyResponseDelay': this.translateService.instant('i18n.report.UnderfrequencyResponseDelay'),
      'report.UnderfrequencySlope': this.translateService.instant('i18n.report.UnderfrequencySlope'),
      'report.UnderfrequencyStart': this.translateService.instant('i18n.report.UnderfrequencyStart'),
      'report.VoltageConfig': this.translateService.instant('i18n.report.VoltageConfig'),
      'report.VoltageDerateResponseTime': this.translateService.instant('i18n.report.VoltageDerateResponseTime'),
      'report.VoltageDropoutPTCurveEnable': this.translateService.instant('i18n.report.VoltageDropoutPTCurveEnable'),
      'report.VoltHighLimit': this.translateService.instant('i18n.report.VoltHighLimit'),
      'report.VoltLowLimit': this.translateService.instant('i18n.report.VoltLowLimit'),
      'WorkMode.generatorMode': this.translateService.instant('i18n.WorkMode.generatorMode'),
      'WorkMode.notAvailable': this.translateService.instant('i18n.WorkMode.notAvailable'),
      'WorkMode.offGridMode': this.translateService.instant('i18n.WorkMode.offGridMode'),
      'WorkMode.passiveMode': this.translateService.instant('i18n.WorkMode.passiveMode'),
      'WorkMode.peakClippingMode': this.translateService.instant('i18n.WorkMode.peakClippingMode'),
      'WorkMode.spontaneousSelfUseMode': this.translateService.instant('i18n.WorkMode.spontaneousSelfUseMode'),
      'WorkMode.timeOfUseElectricityPriceModel': this.translateService.instant('i18n.WorkMode.timeOfUseElectricityPriceModel'),
      'WorkMode.timingChargingAndDischargingMode': this.translateService.instant('i18n.WorkMode.timingChargingAndDischargingMode'),
    });
  }

  updateBlockTranslateService() {
    this.blockTranslationsService.updateLabels({
      ethChannelConnectionError: this.translateService.instant('i18n.Common.ethChannelConnectionError'),
      mqttChannelConnectionError: this.translateService.instant('i18n.Common.mqttChannelConnectionError'),
      deviceNotFoundError: this.translateService.instant('i18n.Common.deviceNotFoundError'),
      requestFailedError: this.translateService.instant('i18n.Common.requestFailedError'),
      loggerNotFoundError: this.translateService.instant('i18n.Common.loggerNotFoundError'),
      genericErrorMessage: this.translateService.instant('i18n.Common.genericErrorMessage'),
    });
  }

  updateCoreService() {
    this.coreServicesService.updateLabels({
      Apr: this.translateService.instant('i18n.Common.Apr'),
      Aug: this.translateService.instant('i18n.Common.Aug'),
      batteryPercentage: this.translateService.instant('i18n.Common.batteryPercentage'),
      day: this.translateService.instant('i18n.Common.day'),
      Dec: this.translateService.instant('i18n.Common.Dec'),
      duration: this.translateService.instant('i18n.EvchargeTelemetryDailyComponent.duration'),
      energy: this.translateService.instant('i18n.Common.energy'),
      energyCharging: this.translateService.instant('i18n.Common.energyCharging'),
      energyConsuming: this.translateService.instant('i18n.Common.energyConsuming'),
      energyDischarging: this.translateService.instant('i18n.Common.energyDischarging'),
      energyExporting: this.translateService.instant('i18n.Common.energyExporting'),
      energyGenerating: this.translateService.instant('i18n.Common.energyGenerating'),
      energyImporting: this.translateService.instant('i18n.Common.energyImporting'),
      energyDailyIrradiation: this.translateService.instant('i18n.Common.energyDailyIrradiation'),
      energyIrradiation: this.translateService.instant('i18n.MonthlyYearlyEnergyOverview.statisticsIrradiationTitle'),
      fast: this.translateService.instant('i18n.Common.fast'),
      Feb: this.translateService.instant('i18n.Common.Feb'),
      fri: this.translateService.instant('i18n.Common.fri'),
      fromBattery: this.translateService.instant('i18n.Common.fromBattery'),
      fromGrid: this.translateService.instant('i18n.Common.fromGrid'),
      fromSolar: this.translateService.instant('i18n.Common.fromSolar'),
      green: this.translateService.instant('i18n.Common.green'),
      greenMixed: this.translateService.instant('i18n.Common.greenMixed'),
      Jan: this.translateService.instant('i18n.Common.Jan'),
      Jul: this.translateService.instant('i18n.Common.Jul'),
      Jun: this.translateService.instant('i18n.Common.Jun'),
      kilometer: this.translateService.instant('i18n.Common.kilometer'),
      Mar: this.translateService.instant('i18n.Common.Mar'),
      maxTh: this.translateService.instant('i18n.Common.maxThreshold'),
      May: this.translateService.instant('i18n.Common.May'),
      minTh: this.translateService.instant('i18n.Common.minThreshold'),
      mon: this.translateService.instant('i18n.Common.mon'),
      month: this.translateService.instant('i18n.Common.month'),
      noConnection: this.translateService.instant('i18n.Common.noConnection'),
      Nov: this.translateService.instant('i18n.Common.Nov'),
      Oct: this.translateService.instant('i18n.Common.Oct'),
      percentage: this.translateService.instant('i18n.Common.percentage'),
      power: this.translateService.instant('i18n.Common.power'),
      powerCharging: this.translateService.instant('i18n.Common.powerCharging'),
      powerConsuming: this.translateService.instant('i18n.Common.powerConsuming'),
      powerDischarging: this.translateService.instant('i18n.Common.powerDischarging'),
      powerExporting: this.translateService.instant('i18n.Common.powerExporting'),
      powerGenerating: this.translateService.instant('i18n.Common.powerGenerating'),
      powerImporting: this.translateService.instant('i18n.Common.powerImporting'),
      powerState: this.translateService.instant('i18n.Common.powerState'),
      sat: this.translateService.instant('i18n.Common.sat'),
      selfConsume: this.translateService.instant('i18n.Common.selfConsumePieDescription'),
      selfProduction: this.translateService.instant('i18n.Common.selfProductionPieDescription'),
      Sep: this.translateService.instant('i18n.Common.Sep'),
      sessionEnergy: this.translateService.instant('i18n.Common.sessionEnergy'),
      singleKilometer: this.translateService.instant('i18n.Common.singleKilometer'),
      singleTimePower: this.translateService.instant('i18n.Common.singleTimePower'),
      strategy: this.translateService.instant('i18n.Common.strategy'),
      sun: this.translateService.instant('i18n.Common.sun'),
      thu: this.translateService.instant('i18n.Common.thu'),
      time: this.translateService.instant('i18n.Common.time'),
      timePower: this.translateService.instant('i18n.Common.timePower'),
      toBattery: this.translateService.instant('i18n.Common.toBattery'),
      toGrid: this.translateService.instant('i18n.Common.toGrid'),
      toHome: this.translateService.instant('i18n.Common.toHome'),
      tue: this.translateService.instant('i18n.Common.tue'),
      value: this.translateService.instant('i18n.Common.value'),
      warning: this.translateService.instant('i18n.Common.warning'),
      wed: this.translateService.instant('i18n.Common.wed'),
    });
  }
}
